import { useState } from "react";
import Card, { SectionTitle } from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Modal from "@/components/base/Modal";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { Input } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { useSchoolYear } from "@/contexts/SchoolYearContext";
import { activateSchoolYear, createSchoolYear, deleteSchoolYear, updateSchoolYear } from "@/services/schoolService";
import { formatDate } from "@/utils/format";
import type { SchoolYear } from "@/types";

interface FormState {
  label: string;
  startDate: string;
  endDate: string;
}

/** Proposition pour la prochaine année : celle qui suit la plus récente (mêmes jours de rentrée et de fin). */
function nextYearDraft(years: SchoolYear[]): FormState {
  const latest = years[0];
  if (!latest) {
    const y = new Date().getFullYear();
    return { label: `${y}-${y + 1}`, startDate: `${y}-10-01`, endDate: `${y + 1}-07-15` };
  }
  const start = Number(latest.label.slice(0, 4)) + 1;
  const shift = (date: string) => `${Number(date.slice(0, 4)) + 1}${date.slice(4)}`;
  return { label: `${start}-${start + 1}`, startDate: shift(latest.startDate), endDate: shift(latest.endDate) };
}

/**
 * Années scolaires : créer, modifier, définir l'année ACTIVE de l'école (celle des inscriptions, de la fiche et
 * des paiements par défaut) et consulter une autre année (les pages suivent l'année choisie dans l'en-tête).
 */
export default function SchoolYearsPanel() {
  const { notify } = useToast();
  const { can } = useAuth();
  const { years, consulted, consult, reload } = useSchoolYear();
  const [editing, setEditing] = useState<SchoolYear | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<FormState>(nextYearDraft(years));
  const [toActivate, setToActivate] = useState<SchoolYear | null>(null);
  const [toDelete, setToDelete] = useState<SchoolYear | null>(null);
  const [busy, setBusy] = useState(false);

  const openForm = (year: SchoolYear | null) => {
    setEditing(year);
    setForm(year ? { label: year.label, startDate: year.startDate, endDate: year.endDate } : nextYearDraft(years));
    setFormOpen(true);
  };

  const run = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    try {
      await action();
      notify(message);
      await reload();
      return true;
    } catch (err) {
      notify(err instanceof Error ? err.message : "Action impossible.", "error");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!/^\d{4}-\d{4}$/.test(form.label.trim())) {
      notify("Le libellé doit être de la forme 2026-2027.", "error");
      return;
    }
    const payload = { label: form.label.trim(), startDate: form.startDate, endDate: form.endDate };
    const ok = await run(
      () => (editing ? updateSchoolYear(editing.id, payload) : createSchoolYear(payload)),
      editing ? `Année ${payload.label} modifiée.` : `Année ${payload.label} créée. Pensez à préparer sa fiche de renseignements.`,
    );
    if (ok) setFormOpen(false);
  };

  return (
    <Card>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <SectionTitle
          icon="ri-calendar-2-line"
          title="Années scolaires"
          subtitle="L'année active est celle de toute l'école ; chacun peut consulter une autre année depuis l'en-tête"
        />
        {can("school-years:create") && (
          <Button icon="ri-add-line" onClick={() => openForm(null)}>
            Nouvelle année
          </Button>
        )}
      </div>

      <div className="divide-y divide-background-200 rounded-md border border-background-200">
        {years.map((year) => (
          <div key={year.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-foreground-900">
                {year.label}
                {year.active && (
                  <Badge tone="success" icon="ri-checkbox-circle-line">
                    Année active
                  </Badge>
                )}
                {year.label === consulted && !year.active && (
                  <Badge tone="accent" icon="ri-eye-line">
                    Consultée
                  </Badge>
                )}
              </p>
              <p className="text-xs text-foreground-500">
                Du {formatDate(year.startDate)} au {formatDate(year.endDate)}
              </p>
            </div>
            {year.label !== consulted && (
              <Button size="sm" variant="outline" icon="ri-eye-line" onClick={() => consult(year.active ? null : year.label)}>
                Consulter
              </Button>
            )}
            {!year.active && can("school-years:activate") && (
              <Button size="sm" variant="outline" icon="ri-checkbox-circle-line" onClick={() => setToActivate(year)}>
                Définir comme active
              </Button>
            )}
            {can("school-years:update") && (
              <Button size="sm" variant="ghost" icon="ri-edit-line" onClick={() => openForm(year)}>
                Modifier
              </Button>
            )}
            {!year.active && can("school-years:delete") && (
              <Button size="sm" variant="ghost" icon="ri-delete-bin-6-line" onClick={() => setToDelete(year)}>
                Supprimer
              </Button>
            )}
          </div>
        ))}
        {years.length === 0 && <p className="px-4 py-6 text-center text-sm text-foreground-400">Aucune année scolaire.</p>}
      </div>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? `Modifier l'année ${editing.label}` : "Nouvelle année scolaire"}
        icon="ri-calendar-2-line"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              Annuler
            </Button>
            <Button icon="ri-save-3-line" onClick={save} disabled={busy}>
              Enregistrer
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Libellé" required placeholder="2026-2027" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Rentrée" type="date" required value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
            <Input label="Fin d'année" type="date" required value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
          </div>
          <p className="text-xs text-foreground-500">
            Les dates servent à rattacher les recettes, les dépenses et les rapports à l'année.
          </p>
        </div>
      </Modal>

      <Modal
        open={toActivate !== null}
        onClose={() => setToActivate(null)}
        title={`Définir ${toActivate?.label ?? ""} comme année active ?`}
        icon="ri-checkbox-circle-line"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setToActivate(null)}>
              Annuler
            </Button>
            <Button
              icon="ri-checkbox-circle-line"
              disabled={busy}
              onClick={async () => {
                const year = toActivate;
                setToActivate(null);
                if (!year) return;
                consult(null);
                await run(() => activateSchoolYear(year.id), `${year.label} est maintenant l'année active de l'école.`);
              }}
            >
              Définir comme active
            </Button>
          </>
        }
      >
        <p className="text-sm text-foreground-600">
          Toute l'école passe sur cette année : inscriptions, fiche de renseignements et paiements l'utiliseront par
          défaut. Les données des autres années restent consultables depuis l'en-tête.
        </p>
      </Modal>

      <ConfirmDialog
        open={toDelete !== null}
        title={`Supprimer l'année ${toDelete?.label ?? ""} ?`}
        message="Possible seulement si aucune inscription, aucun frais ni aucun paiement n'y est rattaché."
        confirmLabel="Supprimer"
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          const year = toDelete;
          setToDelete(null);
          if (!year) return;
          if (year.label === consulted) consult(null);
          await run(() => deleteSchoolYear(year.id), `Année ${year.label} supprimée.`);
        }}
      />
    </Card>
  );
}
